import { Suspense } from "react";
import { createBrowserRouter, useLocation } from "react-router";
import { ProtectedRoute } from "./components/ProtectedRoute";
import { RouteErrorBoundary } from "./components/ui/ErrorBoundary";
import { lazyWithRetry } from "../utils/lazyWithRetry";

// ── Eagerly loaded (tiny, always needed) ─────────────────────────────────────
import { SellerLayout } from "./layouts/SellerLayout";
import { SEO } from "./components/SEO";
import { ErrorPage } from "./pages/ErrorPage";
import { ComingSoon } from "./components/ui/ComingSoon";

// ── Public pages (lazy + retry) ───────────────────────────────────────────────
const LandingPage           = lazyWithRetry(() => import("./pages/LandingPage").then(m => ({ default: m.LandingPage })));
const PrivacyPolicy         = lazyWithRetry(() => import("./pages/PrivacyPolicy").then(m => ({ default: m.PrivacyPolicy })));
const TermsOfService        = lazyWithRetry(() => import("./pages/TermsOfService").then(m => ({ default: m.TermsOfService })));
const ReceiptPage           = lazyWithRetry(() => import("./pages/ReceiptPage").then(m => ({ default: m.ReceiptPage })));
const Storefront            = lazyWithRetry(() => import("./pages/shop/Storefront").then(m => ({ default: m.Storefront })));
const AboutFinancialPassport = lazyWithRetry(() => import("./pages/AboutFinancialPassport").then(m => ({ default: m.AboutFinancialPassport })));
const PublicProfile         = lazyWithRetry(() => import("./pages/shop/PublicProfile").then(m => ({ default: m.PublicProfile })));
const Checkout              = lazyWithRetry(() => import("./pages/shop/Checkout").then(m => ({ default: m.Checkout })));
const OrderSuccess          = lazyWithRetry(() => import("./pages/shop/OrderSuccess").then(m => ({ default: m.OrderSuccess })));
const PaymentPage           = lazyWithRetry(() => import("./pages/shop/PaymentPage").then(m => ({ default: m.PaymentPage })));
const MarketplaceHub        = lazyWithRetry(() => import("./pages/shop/MarketplaceHub").then(m => ({ default: m.MarketplaceHub })));
const VerifyPassport        = lazyWithRetry(() => import("./pages/VerifyPassport").then(m => ({ default: m.VerifyPassport })));

// ── Auth pages (lazy + retry) ─────────────────────────────────────────────────
const SellerLogin           = lazyWithRetry(() => import("./pages/seller/Login").then(m => ({ default: m.SellerLogin })));
const SellerRegister        = lazyWithRetry(() => import("./pages/seller/Register").then(m => ({ default: m.SellerRegister })));
const SellerForgotPassword  = lazyWithRetry(() => import("./pages/seller/ForgotPassword").then(m => ({ default: m.SellerForgotPassword })));
const SellerResetPassword   = lazyWithRetry(() => import("./pages/seller/ResetPassword").then(m => ({ default: m.SellerResetPassword })));

// ── Seller pages (lazy + retry) ───────────────────────────────────────────────
const SellerDashboard       = lazyWithRetry(() => import("./pages/seller/Dashboard").then(m => ({ default: m.SellerDashboard })));
const SellerProducts        = lazyWithRetry(() => import("./pages/seller/Products").then(m => ({ default: m.SellerProducts })));
const SellerOrders          = lazyWithRetry(() => import("./pages/seller/Orders").then(m => ({ default: m.SellerOrders })));
const SellerCreateOrder     = lazyWithRetry(() => import("./pages/seller/CreateOrder").then(m => ({ default: m.SellerCreateOrder })));
const SellerOrderDetail     = lazyWithRetry(() => import("./pages/seller/OrderDetail").then(m => ({ default: m.SellerOrderDetail })));
const SellerEditOrder       = lazyWithRetry(() => import("./pages/seller/EditOrder").then(m => ({ default: m.SellerEditOrder })));
const SellerCustomers       = lazyWithRetry(() => import("./pages/seller/Customers").then(m => ({ default: m.SellerCustomers })));
const SellerAbandonedCarts  = lazyWithRetry(() => import("./pages/seller/AbandonedCarts").then(m => ({ default: m.AbandonedCarts })));
const SellerReports         = lazyWithRetry(() => import("./pages/seller/Reports").then(m => ({ default: m.SellerReports })));
const SellerSettings        = lazyWithRetry(() => import("./pages/seller/Settings").then(m => ({ default: m.SellerSettings })));
const SellerSubscription    = lazyWithRetry(() => import("./pages/seller/Subscription").then(m => ({ default: m.default })));
const TeamSettings          = lazyWithRetry(() => import("./pages/seller/TeamSettings").then(m => ({ default: m.TeamSettings })));
const SellerNotifications   = lazyWithRetry(() => import("./pages/seller/Notifications").then(m => ({ default: m.SellerNotifications })));
const SellerStatuses        = lazyWithRetry(() => import("./pages/seller/Statuses").then(m => ({ default: m.SellerStatuses })));
const FinancialPassport     = lazyWithRetry(() => import("./pages/seller/FinancialPassport").then(m => ({ default: m.FinancialPassport })));
const SellersAdmin          = lazyWithRetry(() => import("./pages/seller/SellersAdmin").then(m => ({ default: m.SellersAdmin })));
const CatalogModeration     = lazyWithRetry(() => import("./pages/seller/CatalogModeration").then(m => ({ default: m.CatalogModeration })));
const SellerExpenses        = lazyWithRetry(() => import("./pages/seller/Expenses").then(m => ({ default: m.SellerExpenses })));
const SellerCoupons         = lazyWithRetry(() => import("./pages/seller/Coupons").then(m => ({ default: m.default })));
const SellerWallet          = lazyWithRetry(() => import("./pages/seller/Wallet").then(m => ({ default: m.SellerWallet })));
const FundWallet            = lazyWithRetry(() => import("./pages/seller/FundWallet").then(m => ({ default: m.FundWallet })));
const SendMoney             = lazyWithRetry(() => import("./pages/seller/SendMoney").then(m => ({ default: m.SendMoney })));
const KYCVerification       = lazyWithRetry(() => import("./pages/seller/KYCVerification").then(m => ({ default: m.KYCVerification })));
const WithdrawFunds         = lazyWithRetry(() => import("./pages/seller/Withdraw").then(m => ({ default: m.WithdrawFunds })));
const CNPLDashboard         = lazyWithRetry(() => import("./pages/seller/CNPLDashboard").then(m => ({ default: m.CNPLDashboard })));
const SellerLeads           = lazyWithRetry(() => import("./pages/seller/Leads").then(m => ({ default: m.Leads })));
const StorefrontAnalytics   = lazyWithRetry(() => import("./pages/seller/Analytics").then(m => ({ default: m.StorefrontAnalytics })));

// ── Loading spinner shown while a lazy chunk is fetching ──────────────────────
function PageLoader() {
  return (
    <div className="min-h-screen flex items-center justify-center bg-[#F3F4F6]">
      <div className="flex flex-col items-center gap-4">
        <div className="w-10 h-10 rounded-xl bg-[#0B6E4F] flex items-center justify-center shadow-lg">
          <span className="text-white font-bold text-xl">M</span>
        </div>
        <div className="flex gap-1.5">
          {[0, 1, 2].map((i) => (
            <div
              key={i}
              className="w-2 h-2 bg-[#0B6E4F] rounded-full animate-bounce"
              style={{ animationDelay: `${i * 0.15}s` }}
            />
          ))}
        </div>
      </div>
    </div>
  );
}

// ── Suspense + RouteErrorBoundary wrapper ─────────────────────────────────────
// The RouteErrorBoundary catches both runtime errors AND failed dynamic imports.
// resetKey = pathname so the boundary auto-clears when navigating away.
function S({ children }: { children: React.ReactNode }) {
  const location = useLocation();
  return (
    <RouteErrorBoundary resetKey={location.pathname}>
      <Suspense fallback={<PageLoader />}>{children}</Suspense>
    </RouteErrorBoundary>
  );
}

import { Outlet } from "react-router";
import { CookieBanner } from "./components/cookies/CookieBanner";

// Root component — provides cookie banner and outlet for all child routes
function Root() {
  return (
    <>
      <CookieBanner />
      <Outlet />
    </>
  );
}

export const router = createBrowserRouter([
  {
    path: "/",
    element: <Root />,
    errorElement: <ErrorPage />,
    children: [
      { path: "/store/:slug",          element: <S><PublicProfile /></S> },
      { path: "/store/:slug/checkout", element: <S><Checkout /></S> },
      { path: "/store/:slug/success",  element: <S><OrderSuccess /></S> },
      { index: true,                   element: <S><LandingPage /></S> },
      { path: "pay/:orderId",          element: <S><PaymentPage /></S> },
      { path: "privacy",               element: <S><PrivacyPolicy /></S> },
      { path: "terms",                 element: <S><TermsOfService /></S> },
      { path: "receipt/:orderId",      element: <S><ReceiptPage /></S> },
      { path: "about/financial-passport", element: <S><AboutFinancialPassport /></S> },
      { path: "help/financial-passport",  element: <S><AboutFinancialPassport /></S> },
      { path: "shop/:sellerId",        element: <S><Storefront /></S> },
      { path: "marketplace",           element: <S><MarketplaceHub /></S> },
      { path: "verify-passport",       element: <S><VerifyPassport /></S> },
      { path: "seller/login",          element: <S><SellerLogin /></S> },
      { path: "seller/register",       element: <S><SellerRegister /></S> },
      { path: "seller/forgot-password", element: <S><SellerForgotPassword /></S> },
      { path: "seller/reset-password",  element: <S><SellerResetPassword /></S> },
      {
        path: "seller",
        element: (
          <ProtectedRoute>
            <SellerLayout />
          </ProtectedRoute>
        ),
        children: [
          { index: true,              element: <S><SellerDashboard /></S> },
          { path: "products",         element: <S><SellerProducts /></S> },
          { path: "orders",           element: <S><SellerOrders /></S> },
          { path: "orders/new",       element: <S><SellerCreateOrder /></S> },
          { path: "orders/:id",       element: <S><SellerOrderDetail /></S> },
          { path: "orders/:id/edit",  element: <S><SellerEditOrder /></S> },
          { path: "customers",        element: <S><SellerCustomers /></S> },
          { path: "leads",            element: <S><SellerLeads /></S> },
          { path: "abandoned-carts",  element: <S><SellerAbandonedCarts /></S> },
          { path: "reports",          element: <S><SellerReports /></S> },
          { path: "settings",         element: <S><SellerSettings /></S> },
          { path: "settings/team",    element: <S><TeamSettings /></S> },
          { path: "subscription",     element: <S><SellerSubscription /></S> },
          { path: "notifications",    element: <S><SellerNotifications /></S> },
          { path: "statuses",         element: <S><SellerStatuses /></S> },
          { path: "passport",         element: <S><FinancialPassport /></S> },
          { path: "admin/sellers",    element: <S><SellersAdmin /></S> },
          { path: "admin/catalog",    element: <S><CatalogModeration /></S> },
          { path: "expenses",         element: <S><SellerExpenses /></S> },
          { path: "coupons",          element: <S><SellerCoupons /></S> },
          { path: "wallet",           element: (import.meta as any).env.PROD ? <ComingSoon /> : <S><SellerWallet /></S> },
          { path: "wallet/fund",      element: (import.meta as any).env.PROD ? <ComingSoon /> : <S><FundWallet /></S> },
          { path: "wallet/send",      element: (import.meta as any).env.PROD ? <ComingSoon /> : <S><SendMoney /></S> },
          { path: "wallet/withdraw",  element: (import.meta as any).env.PROD ? <ComingSoon /> : <S><WithdrawFunds /></S> },
          { path: "cnpl",             element: <S><CNPLDashboard /></S> },
          { path: "analytics",        element: <S><StorefrontAnalytics /></S> },
        ],
      },
      { path: "*", element: <ErrorPage /> },
    ],
  },
]);
